import { Fragment }                    from 'react';
import { getBusinessPlaceID }          from './display_helpers';
import { getGoogleLocationAddress }    from './display_helpers';
import { getGoogleLocationIsVerified } from './display_helpers';
import { getGoogleLocationPlaceID }    from './display_helpers';
import { isServiceAreaLocation }       from './display_helpers';
import { locationSources }             from './gmb_api';
import { useAllLocations }             from './gmb_api';
import { useClient }                   from './google_auth';
import { useState }                    from 'react';
import GoogleAuth                      from './google_auth';
import React                           from 'react';
import rollbar                         from '../../rollbar';
import Swal                            from 'sweetalert2';


export default function ChooseLocationView({
  businessID,
  business,
  statuses,
  saveGoogleLocation,
  navigateTo,
  navigateBackTo,
  progressStart,
  progressEnd
}) {

  const [ offlineAccessCode, setOfflineAccessCode ] = useState();

  const saveLocation = createSaveLocationCallback({
    businessID,
    navigateTo,
    navigateBackTo,
    offlineAccessCode,
    saveGoogleLocation,
    showAlert: options => Swal.fire(options)
  });

  return (
    <GoogleAuth requireReauthorization={true}>
      <GoogleAuth.Loading>
        <GoogleAuth.LoadingView/>
      </GoogleAuth.Loading>
      <GoogleAuth.Failed>
        <GoogleAuth.FailedView/>
      </GoogleAuth.Failed>
      <GoogleAuth.SignedOut>
        <AnonymousView {...{ setOfflineAccessCode }}/>
      </GoogleAuth.SignedOut>
      <GoogleAuth.SignedIn>
        <AuthedView {...{
          business,
          statuses,
          setOfflineAccessCode,
          saveLocation,
          progressStart,
          progressEnd
        }}/>
      </GoogleAuth.SignedIn>
    </GoogleAuth>
  );
}

export function createSaveLocationCallback({
  businessID,
  navigateTo,
  navigateBackTo,
  offlineAccessCode,
  saveGoogleLocation,
  showAlert
}) {
  return async googleLocation => {
    try {
      await saveGoogleLocation({
        googleLocation,
        offlineAccessCode
      });

      const isVerified    = getGoogleLocationIsVerified(googleLocation);
      const isServiceArea = isServiceAreaLocation(googleLocation);
      if (isVerified) {
        navigateBackTo(`/${businessID}/settings/business_profile/google`);
        showAlert({
          text: 'Your location has been connected!',
          icon: 'success'
        });
      } else if (isServiceArea)
        navigateTo(`/${businessID}/settings/business_profile/google/address`);
      else
        navigateTo(`/${businessID}/settings/business_profile/google/verify`);

    } catch (error) {
      console.error(error);
      // showing the error is handled by the saveGoogleLocation action
      // but the thrown error prevents our navigate call, which is good
    }
  };
}

function AnonymousView({ setOfflineAccessCode }) {
  const client = useClient();

  async function onClickSignIn() {
    const { code } = await client.grantOfflineAccess();
    setOfflineAccessCode(code);
  }

  return (
    <Fragment>
      <div className="list block inset">
        <h3>Google My Business</h3>
        <p>Connect your Google business location to your Broadly account to manage your business listing, receive real-time reviews, and more.</p>
      </div>
      <div className="list block inset google-signin">
        <p>
          <button className="button button-fill" onClick={onClickSignIn}>
            Sign in with your Google Account
          </button>
        </p>
      </div>
    </Fragment>
  );
}

function AuthedView({
  business,
  statuses,
  setOfflineAccessCode,
  saveLocation,
  progressStart,
  progressEnd
}) {
  const { result, resultStatus } = useAllLocations({ progressStart, progressEnd });

  if (resultStatus === 'pending')
    return null;
  else if (resultStatus === 'failed')
    return <LocationsFailedView/>;
  else {
    return <LocationResultView {...{
      business,
      statuses,
      result,
      setOfflineAccessCode,
      saveLocation
    }}/>;
  }
}

export function LocationResultView({
  business,
  statuses,
  result,
  setOfflineAccessCode,
  saveLocation
}) {
  const currentPlaceID = getBusinessPlaceID(business);
  const items          = getLocationListForDisplay(result, currentPlaceID);

  if (!items.length)
    return <NoLocationsView {...{ setOfflineAccessCode }}/>;

  return (
    <Fragment>
      <LocationListView {...{ items, saveLocation, statuses }}/>
      <OtherChoicesWithTitle {...{ setOfflineAccessCode }}/>
    </Fragment>
  );
}

function LocationListView({ items, saveLocation, statuses }) {
  const { updateStatus } = statuses;
  const { pending }      = updateStatus;

  return (
    <div className="list media-list block inset location-chooser">
      <div className="list-group">
        <ul>
          <li className="list-group-title">Choose your location:</li>
          {items.map(item => <LocationItemView key={item.location.name} {...{ item, saveLocation, pending }}/>)}
        </ul>
      </div>
    </div>
  );
}

function LocationItemView({ item, saveLocation, pending }) {
  const { location }     = item;
  const { locationName } = location;
  const address          = getGoogleLocationAddress(location);

  function onClick() {
    saveLocation(location);
  }

  return (
    <li>
      <div className="item-content">
        <div className="item-inner">
          <div className="item-title-row">
            <div className="item-title">
              {locationName}
            </div>
          </div>
          <div className="item-subtitle">
            {address}
          </div>
        </div>
      </div>
      <div className="item-after">
        <button className="button button-fill" disabled={pending} onClick={onClick}>
          Connect
        </button>
      </div>
    </li>
  );
}

function OtherChoicesWithTitle({ setOfflineAccessCode }) {
  return (
    <div className="list media-list block inset">
      <div className="list-group">
        <ul>
          <li className="list-group-title">Don’t see your location?</li>
        </ul>
        <OtherChoices {...{ setOfflineAccessCode }}/>
      </div>
    </div>
  );
}

function NoLocationsView({ setOfflineAccessCode }) {
  return (
    <div className="list media-list block inset no-locations-found">
      <p>There are no locations associated with your Google account.</p>
      <OtherChoices {...{ setOfflineAccessCode }}/>
    </div>
  );
}

export function LocationsFailedView({ setOfflineAccessCode }) {
  return (
    <div className="list media-list block inset no-locations-found">
      <p>There was an error fetching the locations associated with your Google account.</p>
      <OtherChoices {...{ setOfflineAccessCode }}/>
    </div>
  );
}

function OtherChoices({ setOfflineAccessCode }) {
  const client = useClient();

  async function signInWithDifferentAccount() {
    client.signOut();
    const { code } = await client.grantOfflineAccess();
    setOfflineAccessCode(code);
  }

  return (
    <p>
      <button className="button button-fill" onClick={signInWithDifferentAccount}>
        Sign in with a different account
      </button>
    </p>
  );
}


export function getLocationListForDisplay(results, currentPlaceID) {
  const locations = results
    .map(getLocationFromResult)
    .filter(item => item)
    .reduce(uniqBy('placeID'), [])
    .sort(sortByLocationName)
    .sort(sortBySource(locationSources))
    .sort(sortCurrentPlaceIDFirst(currentPlaceID));

  return locations;
}

function getLocationFromResult({ location, source }) {
  try {
    return {
      name:         location.name,
      locationName: location.locationName,
      address:      getGoogleLocationAddress(location),
      placeID:      getGoogleLocationPlaceID(location),
      isVerified:   getGoogleLocationIsVerified(location),
      source,
      location
    };
  } catch (error) {
    rollbar.error(error);
    return null;
  }
}

function uniqBy(attribute) {
  const added = new Set();
  return function(memo, item) {
    const value = item[attribute];
    // don't filter if the attribute is empty
    if (!value)
      memo.push(item);
    else if (!added.has(value)) {
      added.add(value);
      memo.push(item);
    }
    return memo;
  };
}

function sortByLocationName(a, b) {
  const aName = a.locationName.toLowerCase();
  const bName = b.locationName.toLowerCase();
  if (aName < bName)
    return -1;
  else if (aName > bName)
    return 1;
  else
    return 0;
}

function sortBySource(sources) {
  const sourceNames = Object.keys(sources);
  return function(a, b) {
    const indexOfA = sourceNames.indexOf(a);
    const indexOfB = sourceNames.indexOf(b);
    return indexOfA - indexOfB;
  };
}

function sortCurrentPlaceIDFirst(currentPlaceID) {
  return (a, b) => {
    if (currentPlaceID && a.placeID === currentPlaceID)
      return -1;
    if (currentPlaceID && b.placeID === currentPlaceID)
      return 1;
    return 0;
  };
}
